import  React, { useState } from 'react'; 
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, message, Input, Space, Layout, Spin, Typography } from 'antd';
import { DatabaseOutlined } from '@ant-design/icons';
import useIsLoading from '../../hooks/useIsLoading';  
import useHasError from '../../hooks/useHasError'; 
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import AIReportView from '../../components/AIReportView/AIReportView';
import AISavePrompt from '../../components/AISavePrompt/AISavePrompt';
import { Routes } from '../../../routes';  
import useAICreatePrompt from '../../gql/hooks/useAICreatePrompt';
import useAICreatePromptReport from '../../gql/hooks/useAICreatePromptReport';
import './styles.less'; 

const AIReportCreatePage = ( props ) => {     
    const [ reportId, setReportId ] = useState( );
    const [ promptId, setPromptId ] = useState( );
    const [ promptText, setPromptText ] = useState( '' );
    const [ UIPromptText, setUIPromptText ] = useState( false );
    const [ showExamples, setShowExamples ] = useState( false );
    const [ generateButtonEnabled, setGenerateButtonEnabled ] = useState( false );
    const { createPrompt } = useAICreatePrompt();
    const { createPromptReport } = useAICreatePromptReport();
    const [ generatingPrompt, setGeneratingPrompt ] = useState( false );
    const { isLoading } = useIsLoading( { items: [ generatingPrompt ] } );
    const { hasError } = useHasError( { items: [] } );
    const [ AIerror, setAIError ] = useState( false );
    const [ messageApi, contextHolder ] = message.useMessage();

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Analytics', path: Routes.Reporting.buildPath() },
        { name: 'AI Reporting', path: Routes.AIReport.buildPath() },
        { name: 'Create Prompt' }
    ];
 
    const extractGraphQLErrorMessage = (  resp ) => {
        const defaultMsg = 'Error creating prompt. Please try again later.';
        const gqlErrors = resp?.response?.graphQLErrors;
        if ( !gqlErrors || gqlErrors.length === 0 ) {
            return defaultMsg;
        }
        const first = gqlErrors[ 0 ];
        const errs = first?.extensions?.response?.body?.errors;
        if ( !Array.isArray( errs ) || errs.length === 0 ) return defaultMsg;
        return errs.map( e => e?.message || String( e ) ).join( '\n' );
    };

    /*************************************
     *  ANCHOR Event handlers
     *************************************/
 
    const handlePromptChange = ( e ) => {
        const value = e.target.value.trim();
        setUIPromptText( value );
        setGenerateButtonEnabled( value.length > 0 );
    };

    const handleGenerateClick = async () => {
        setGeneratingPrompt( true );
        setAIError( false );

        // Create prompt
        const response = await createPrompt( { prompt: UIPromptText } );
        
        if ( response.success ) {
            setPromptId( response.response.data.createPrompt.id );
            setPromptText(  response.response.data.createPrompt.prompt );
        } else {

            setAIError( extractGraphQLErrorMessage( response ) );
 
            messageApi.error( 'Error creating prompt. Please try again later.' );
            setGeneratingPrompt( false );
            return;
        }
        
        // Create report
        const responseReport = await createPromptReport( response.response.data.createPrompt.id );
        
        if ( responseReport.success ) {
            setReportId( responseReport.response.data.promptReport.id ); 
        } else {
            messageApi.error( 'Error creating prompt report. Please try again later.' );
            setGeneratingPrompt( false );
            return;
        }
        
        setGeneratingPrompt( false );
    };
    
    const handleToggleExamplesClick = () => {
        setShowExamples( !showExamples );
    };

    const handleCopyText = ( text ) => {
        navigator.clipboard.writeText( text );
    };

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return ( 
        <Layout className="ai-report-create-page">
            { contextHolder }

            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>
 
            { /* Main Content */ }
            <Spin spinning={ isLoading }>
                <Card
                    title="Create Prompt"
                    className="ai-report-create-content"
                >   
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }
                    
                    { AIerror && 
                        <Alert 
                            type="error"
                            message="Error"
                            description={ AIerror }
                        />
                    }

                    { !hasError && 
                        <>
                            <h4>AI Prompt</h4>
                            <Input.TextArea   
                                className="prompt-input"
                                placeholder="Enter your AI prompt here, click below for example prompts..." 
                                allowClear
                                bordered={ true }
                                rows={ 8 } 
                                maxLength={ 2000 }
                                onChange={ handlePromptChange }
                            />
                            
                            <div className="generate-parent">
                                <div className="left">
                                    <Button    
                                        type="dashed"
                                        onClick= { () => handleToggleExamplesClick() }
                                    >
                                        Example Prompts 
                                        { showExamples ? <CaretUpOutlined /> : <CaretDownOutlined /> }
                                    </Button>
                                </div>

                                <div className="right">
                                    <Space>
                                        <AISavePrompt 
                                            disabled={ !promptId || promptText !== UIPromptText }
                                            promptId={ promptId } 
                                        />

                                        <Button
                                            type="primary"
                                            disabled={ !generateButtonEnabled }
                                            icon={ <DatabaseOutlined /> } 
                                            onClick= { () => handleGenerateClick() }
                                        >
                                            Generate Report
                                        </Button>
                                    </Space>
                                </div>
                            </div>

                            { showExamples &&
                                <div className="example-prompts">
                                    <Alert
                                        message="AI Prompt Examples"
                                        description={
                                            <>
                                                <p>Here are some examples of prompts you can use to generate reports. 
                                                You can use plain language as if you were talking to a person and ask for any data that system has.</p>                                         
                                                <ul>
                                                    <li className="example-prompt-li">
                                                        <div className="left">
                                                            <Typography.Text italic>Show me the most active users within the last month.</Typography.Text>
                                                        </div>
                                                        <div className="right">
                                                            <Button
                                                                className="copy-button"
                                                                type="dashed"
                                                                size="small"
                                                                onClick={ () => handleCopyText( 'Show me the most active users within the last month.' ) }
                                                            >
                                                                Copy
                                                            </Button>
                                                        </div>
                                                    </li>
                                                    <li className="example-prompt-li">
                                                        <div className="left">
                                                            <Typography.Text italic>Generate a report showing the most completed courses.</Typography.Text>
                                                        </div>
                                                        <div className="right">
                                                            <Button
                                                                className="copy-button"
                                                                type="dashed"
                                                                size="small"
                                                                onClick={ () => handleCopyText( 'Generate a report showing the most completed courses.' ) }
                                                            >
                                                            Copy
                                                            </Button>
                                                        </div>
                                                    </li>                                        
                                                    <li className="example-prompt-li">
                                                        <div className="left">
                                                            <Typography.Text italic>The number of users that have course completions for each month of the last 6 months.</Typography.Text>
                                                        </div>
                                                        <div className="right">
                                                            <Button
                                                                className="copy-button"
                                                                type="dashed"
                                                                size="small"
                                                                onClick={ () => handleCopyText( 'The number of users that have course completions for each month of the last 6 months.' ) }
                                                            >
                                                            Copy
                                                            </Button>
                                                        </div>
                                                    </li>                                        
                                                </ul>
                                            </>
                                        }
                                        type="warning"
                                    />
                                </div>
                            }

                            <div className="report-view">
                                { reportId && promptId &&
                                    <AIReportView
                                        pageSize={ 10 } 
                                        reportId={ reportId }
                                        promptId={ promptId }
                                    />
                                }
                            </div>
                        </>
                    }
                </Card>
            </Spin>
        </Layout>
    );
};

export default AIReportCreatePage;
